import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import AIService from "../../../services/AIService";
import { Send, X, ChevronRight, ImagePlus } from "lucide-react";

const STARTER_PROMPTS = [
  "Farmers market, June 14 at Riverside Park, 50 vendors",
  "Craft fair, 60 vendors, $50 booth fee, next August",
  "Artisan festival — food, jewelry, and plants",
];

const PebbleMark = () => (
  <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="5" fill="currentColor" />
    <circle cx="12" cy="5" r="2.5" fill="currentColor" opacity="0.5" />
    <circle cx="12" cy="19" r="2.5" fill="currentColor" opacity="0.5" />
    <circle cx="5" cy="12" r="2.5" fill="currentColor" opacity="0.5" />
    <circle cx="19" cy="12" r="2.5" fill="currentColor" opacity="0.5" />
  </svg>
);

function renderMessage(text) {
  return text.split('\n').map((line, i, arr) => (
    <span key={i}>
      {line.split(/\*\*([^*]+)\*\*/g).map((part, j) =>
        j % 2 === 1 ? <strong key={j}>{part}</strong> : part
      )}
      {i < arr.length - 1 && <br />}
    </span>
  ));
}

const AIEventAssistant = ({ isOpen, onClose, onApplySuggestions }) => {
  const { subscriptionTier } = useAuth();
  const navigate = useNavigate();
  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [extractedFields, setExtractedFields] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [started, setStarted] = useState(false);
  const [, setCurrentStage] = useState('eventType');
  const [flyerUploading, setFlyerUploading] = useState(false);
  const [, setFlyerPreview] = useState(null); // data URL for preview
  const [flyerFile, setFlyerFile] = useState(null); // actual File object for cover photo

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const flyerInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && isPro && !started) {
      startConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isPro]);

  const startConversation = async () => {
    setStarted(true);
    setIsTyping(true);
    await new Promise(r => setTimeout(r, 500));
    setMessages([{ role: "ai", text: "Let's build your event. What type of market or festival are you running, and what's it called?" }]);
    setIsTyping(false);
    inputRef.current?.focus();
  };

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || isTyping) return;

    setInput("");
    const newMessages = [...messages, { role: "user", text: userText }];
    setMessages(newMessages);
    setIsTyping(true);

    try {
      const history = newMessages.map(m => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.text }));
      const result = await AIService.chatEventExtraction(userText, history, extractedFields);

      if (result.success) {
        setExtractedFields(result.extractedFields);
        setIsComplete(result.isComplete);
        setCurrentStage(result.stage);
        setMessages(prev => [...prev, { role: "ai", text: result.aiMessage }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "ai", text: "Something went wrong. Please try again." }]);
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  const handleFlyerUpload = async (file) => {
    if (!file) return;

    // Show image preview immediately in chat
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setFlyerPreview(dataUrl);
      setFlyerFile(file);
      setMessages(prev => [...prev, { role: "user", text: file.name, imageUrl: dataUrl }]);
    };
    reader.readAsDataURL(file);

    setFlyerUploading(true);
    setIsTyping(true);

    try {
      // Use Claude Haiku vision (production) or demo mock
      const scanResult = await AIService.analyzeFlyer(file);

      if (scanResult.success && scanResult.fields) {
        const merged = { ...extractedFields, ...scanResult.fields };
        setExtractedFields(merged);
        setCurrentStage('review');
        setIsComplete(true);

        const allFields = ['eventName','eventType','startDate','location','sellerCapacity','registrationFee','sellerTypes'];
        const missing = allFields.filter(k => !merged[k] || (Array.isArray(merged[k]) && !merged[k].length));
        const summary = formatFieldsSummary(merged);
        const missingLine = missing.length
          ? `\n\nCouldn't find: **${missing.map(k => ({ eventName:'event name', eventType:'event type', startDate:'date', location:'location', sellerCapacity:'vendor count', registrationFee:'booth fee', sellerTypes:'vendor types' })[k]).join(', ')}**. You can add those manually or hit Fill My Form now.`
          : `\n\nReady to fill your form — or add anything else first.`;

        setMessages(prev => [...prev, {
          role: "ai",
          text: `Here's what I pulled from your flyer:\n\n${summary}${missingLine}`,
        }]);
      } else {
        setMessages(prev => [...prev, { role: "ai", text: "I couldn't read that flyer clearly. Try typing your event details instead." }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "ai", text: "Couldn't scan the flyer. Try typing your event details instead." }]);
    } finally {
      setFlyerUploading(false);
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  // Helper to build summary string (mirrors AIService.formatFieldsSummary)
  const formatFieldsSummary = (fields) => {
    const lines = [];
    if (fields.eventName) lines.push(`**${fields.eventName}** (${fields.eventType || 'Event'})`);
    if (fields.startDate) lines.push(`Date: ${fields.startDate}${fields.endDate && fields.endDate !== fields.startDate ? ` – ${fields.endDate}` : ''}`);
    if (fields.location) lines.push(`Location: ${fields.location}`);
    if (fields.sellerCapacity) lines.push(`Vendors: ${fields.sellerCapacity} spots`);
    if (fields.registrationFee) lines.push(`Booth fee: $${fields.registrationFee}`);
    if (fields.sellerTypes?.length) lines.push(`Vendor types: ${fields.sellerTypes.join(', ')}`);
    return lines.join('\n');
  };

  const handleApply = () => {
    if (onApplySuggestions) {
      onApplySuggestions(extractedFields, flyerFile || null);
    }
    onClose();
  };

  if (!isOpen) return null;

  // Pro gate
  if (!isPro) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center">
          <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-white p-2.5">
            <PebbleMark />
          </div>
          <p className="text-xs font-bold text-primary-600 uppercase tracking-widest mb-2">Pro Only</p>
          <h2 className="text-lg font-bold text-grey-900 mb-2">Build your event with AI</h2>
          <p className="text-grey-500 text-sm mb-6">
            Describe your event — or upload a flyer — and Pebble fills in everything for you.
          </p>
          <button
            onClick={() => { onClose(); navigate('/eo/settings?tab=subscription'); }}
            className="w-full py-3 px-6 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors mb-3"
          >
            Upgrade to Pro
          </button>
          <button onClick={onClose} className="text-sm text-grey-400 hover:text-grey-600 transition-colors">
            Fill out manually instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl flex flex-col" style={{ height: '88vh', maxHeight: '720px' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-grey-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-600 rounded-xl flex items-center justify-center text-white p-1.5">
              <PebbleMark />
            </div>
            <div>
              <p className="text-sm font-bold text-grey-900 tracking-tight">Pebble AI</p>
              <p className="text-[11px] text-grey-400 font-medium">Event builder · Pro</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 hover:bg-grey-100 rounded-lg transition-colors">
              <X className="w-4 h-4 text-grey-400" />
            </button>
          </div>
        </div>


        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-10 h-10 bg-primary-600 rounded-2xl text-white p-2 mx-auto mb-3">
                <PebbleMark />
              </div>
              <p className="text-sm text-grey-500">Getting started…</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'ai' && (
                <div className="w-6 h-6 bg-primary-600 rounded-lg text-white flex-shrink-0 mr-2 mt-0.5 p-1">
                  <PebbleMark />
                </div>
              )}
              <div className={`max-w-[80%] ${msg.imageUrl ? '' : 'px-4 py-2.5'} rounded-2xl text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white rounded-tr-sm font-medium'
                  : 'bg-grey-100 text-grey-800 rounded-tl-sm'
              }`}>
                {msg.imageUrl ? (
                  <div className="overflow-hidden rounded-2xl rounded-tr-sm">
                    <img
                      src={msg.imageUrl}
                      alt="Uploaded flyer"
                      className="max-w-[220px] max-h-[280px] object-cover block"
                    />
                    <div className="px-3 py-2 bg-primary-600">
                      <p className="text-xs text-primary-100 font-medium truncate max-w-[200px]">
                        {msg.text.replace('(flyer: ', '').replace(')', '')}
                      </p>
                    </div>
                  </div>
                ) : renderMessage(msg.text)}
              </div>
            </div>
          ))}

          {/* Scanning indicator */}
          {flyerUploading && (
            <div className="flex justify-start">
              <div className="w-6 h-6 bg-primary-600 rounded-lg text-white flex-shrink-0 mr-2 mt-0.5 p-1">
                <PebbleMark />
              </div>
              <div className="bg-grey-100 rounded-2xl rounded-tl-sm px-4 py-3 flex flex-col gap-2 min-w-[180px]">
                <div className="flex items-center gap-2">
                  <div className="w-3.5 h-3.5 border-2 border-grey-300 border-t-primary-600 rounded-full animate-spin flex-shrink-0" />
                  <span className="text-xs font-semibold text-grey-700">Scanning your flyer…</span>
                </div>
                {/* Animated scan progress bar */}
                <div className="w-full bg-grey-200 rounded-full h-1 overflow-hidden">
                  <div className="h-1 bg-primary-600 rounded-full animate-pulse" style={{ width: '70%' }} />
                </div>
                <p className="text-[10px] text-grey-400">Reading event details with AI</p>
              </div>
            </div>
          )}

          {isTyping && !flyerUploading && (
            <div className="flex justify-start">
              <div className="w-6 h-6 bg-primary-600 rounded-lg text-white flex-shrink-0 mr-2 mt-0.5 p-1">
                <PebbleMark />
              </div>
              <div className="bg-grey-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Starter prompts — before first user message */}
        {messages.length === 1 && !isTyping && (
          <div className="px-5 pb-3 flex-shrink-0">
            <p className="text-[11px] font-semibold text-grey-400 uppercase tracking-wide mb-2">Quick start</p>
            <div className="flex flex-col gap-1.5">
              {STARTER_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(p)}
                  className="text-left text-xs text-grey-700 bg-grey-50 hover:bg-grey-100 border border-grey-200 rounded-xl px-3 py-2 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fill form CTA */}
        {isComplete && (
          <div className="px-5 pb-3 flex-shrink-0">
            <button
              onClick={handleApply}
              className="w-full flex items-center justify-center gap-2 py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-colors tracking-tight"
            >
              Fill My Form
              <ChevronRight className="w-4 h-4" />
            </button>
            {flyerFile && (
              <p className="text-[11px] text-grey-400 text-center mt-1.5">
                Your flyer will be set as the event cover photo
              </p>
            )}
          </div>
        )}

        {/* Input */}
        <div className="px-5 py-4 border-t border-grey-100 flex-shrink-0">
          <input
            type="file"
            ref={flyerInputRef}
            accept="image/*,.pdf"
            className="hidden"
            onChange={e => handleFlyerUpload(e.target.files?.[0])}
          />
          <div className="flex items-end gap-2">
            <button
              onClick={() => flyerInputRef.current?.click()}
              disabled={flyerUploading || isTyping}
              title="Upload a flyer or graphic"
              className="p-2.5 bg-grey-100 hover:bg-grey-200 disabled:opacity-40 text-grey-500 hover:text-grey-700 rounded-xl transition-colors flex-shrink-0"
            >
              {flyerUploading
                ? <div className="w-4 h-4 border-2 border-grey-400 border-t-transparent rounded-full animate-spin" />
                : <ImagePlus className="w-4 h-4" />
              }
            </button>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Describe your event or upload a flyer…"
              rows={1}
              className="flex-1 resize-none px-4 py-2.5 bg-grey-50 border border-grey-200 rounded-xl text-sm text-grey-900 placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
              style={{ maxHeight: '100px' }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isTyping}
              className="p-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-30 text-white rounded-xl transition-colors flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-[10px] text-grey-300">Enter to send · Shift+Enter for new line</p>
            <button
              onClick={onClose}
              className="text-[10px] text-grey-400 hover:text-grey-600 underline underline-offset-2 transition-colors"
            >
              Fill out manually instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIEventAssistant;
